import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Lock, Unlock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  useEffect(() => {
    fetchUsers();
  }, [pagination.page]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users?page=${pagination.page}`);
      setUsers(res.data.users);
      setPagination({ page: res.data.page, pages: res.data.pages, total: res.data.total });
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async (userId, updates) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, updates);
      toast.success('User updated');
      fetchUsers();
    } catch (err) {
      toast.error('Failed to update user');
    }
  };

  const unlockUser = async (userId, email) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/unlock`);
      toast.success(`Account unlocked for ${email}`);
      fetchUsers();
    } catch (err) {
      toast.error('Failed to unlock account');
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'wholesale': return 'bg-purple-500/20 text-purple-500';
      case 'trade': return 'bg-[#78BE20]/20 text-[#78BE20]';
      default: return 'bg-zinc-700 text-zinc-400';
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-users">
        <h1 className="text-3xl font-black text-white uppercase tracking-tight mb-8">
          Users
        </h1>

        <div className="bg-[#18181b] border border-zinc-800 rounded-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-zinc-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Role</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Account Tier</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Trade Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Joined</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td colSpan={6} className="px-4 py-4">
                      <div className="h-8 bg-zinc-800 rounded animate-pulse" />
                    </td>
                  </tr>
                ))
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-zinc-500">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map(user => (
                  <tr key={user.id} className="hover:bg-zinc-900/50">
                    <td className="px-4 py-4">
                      <p className="text-white font-medium">{user.first_name} {user.last_name}</p>
                      <p className="text-xs text-zinc-500">{user.email}</p>
                    </td>
                    <td className="px-4 py-4">
                      <Select
                        value={user.role}
                        onValueChange={(v) => updateUser(user.id, { role: v })}
                      >
                        <SelectTrigger className="w-32 bg-zinc-800 border-zinc-700">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="customer">Customer</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-4">
                      <Select
                        value={user.account_tier}
                        onValueChange={(v) => updateUser(user.id, { account_tier: v })}
                      >
                        <SelectTrigger className="w-32 bg-zinc-800 border-zinc-700">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="retail">Retail</SelectItem>
                          <SelectItem value="trade">Trade</SelectItem>
                          <SelectItem value="wholesale">Wholesale</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-4">
                      <Badge className={getTierColor(user.account_tier)}>
                        {user.trade_application_status || 'N/A'}
                      </Badge>
                    </td>
                    <td className="px-4 py-4">
                      {user.account_locked ? (
                        <div className="flex items-center gap-2">
                          <Badge className="bg-red-500/20 text-red-400 flex items-center gap-1" data-testid={`locked-badge-${user.id}`}>
                            <Lock className="h-3 w-3" /> Locked
                          </Badge>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-xs text-zinc-400 hover:text-white"
                            onClick={() => unlockUser(user.id, user.email)}
                            data-testid={`unlock-btn-${user.id}`}
                          >
                            <Unlock className="h-3 w-3 mr-1" /> Unlock
                          </Button>
                        </div>
                      ) : (
                        <Badge className="bg-green-500/20 text-green-400" data-testid={`active-badge-${user.id}`}>
                          Active
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-4 text-zinc-400 text-sm">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination.pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <Button
              variant="outline"
              size="icon"
              disabled={pagination.page <= 1}
              onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
              className="border-zinc-700"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-zinc-400 px-4">
              Page {pagination.page} of {pagination.pages}
            </span>
            <Button
              variant="outline"
              size="icon"
              disabled={pagination.page >= pagination.pages}
              onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
              className="border-zinc-700"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
